import cn from "classnames";
import { Transition } from "components/shared/column";
import { dateTimeToView } from "helpers/date";
import { paths } from "helpers/paths";
import { ymEvent } from "helpers/system";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    cancelKonturIntegration,
    getKonturAuthorizationState,
    initKonturAuthorization,
    type KonturIntegrationState,
} from "sources/profile/organization";

const helpUrl = "https://crg.im/cv";

const emptyIntegrationState: KonturIntegrationState = {
    isAuthenticated: false,
    updatedAt: null,
    email: null,
    isReady: false,
    issue: null,
};

type KonturIntegrationProps = {
    location: {
        state?: {
            konturIntegration?: KonturIntegrationState;
            konturIssue?: string;
        };
    };
};

export default function KonturIntegration({ location }: KonturIntegrationProps) {
    const callbackData = location.state?.konturIntegration;
    const [callbackIssue, setCallbackIssue] = useState(location.state?.konturIssue);
    const [loading, setLoading] = useState(!callbackData);
    const [data, setData] = useState<KonturIntegrationState>(callbackData ?? emptyIntegrationState);
    const fetchData = useCallback(() => {
        return getKonturAuthorizationState()
            .then((response) => {
                if (response) {
                    setData(response);
                    setCallbackIssue(undefined);
                }
            })
            .catch(() => undefined)
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleConnect = () => {
        ymEvent("ConnectKonturIntegration");
        setLoading(true);
        initKonturAuthorization({})
            .then((res) => {
                if (res?.url) {
                    window.location = res.url;
                } else {
                    setLoading(false);
                }
            })
            .catch(() => setLoading(false));
    };

    const handleDelete = () => {
        setLoading(true);
        cancelKonturIntegration()
            .then(() => fetchData())
            .catch(() => setLoading(false));
    };

    const issue = data.issue ?? callbackIssue;
    const isActive = data.isAuthenticated && !issue;

    return (
        <Transition dir="right">
            <div className="page-book__show book-show">
                <div className="book-show__head">
                    <Link className="book-show__close close close--sm" to={paths.integration} />
                    <div className="book-show__title">Документооборот (Контур.Диадок)</div>
                </div>
                <div className={cn("book-show__body", { loading })}>
                    <div className="d-flex flex-column">
                        <div className="card card-body font-size-14 py-2 mb-3">
                            <div className="mb-3">
                                Подключение личного кабинета для авторизации в системе <b>&quot;Контур.Диадок&quot;</b>
                            </div>
                            <table className="table table-sm col-sm-6 mb-5">
                                <tbody>
                                    <tr>
                                        <td className="noborder">Статус интеграции</td>
                                        <td className="noborder">
                                            <span
                                                className={cn("badge", {
                                                    "badge-success": isActive,
                                                    "badge-danger": !isActive,
                                                })}
                                            >
                                                {isActive ? "Активен" : "Не активна"}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Эл. почта</td>
                                        <td>{data.email ?? <>&mdash;</>}</td>
                                    </tr>
                                    <tr>
                                        <td>Дата последнего обновления</td>
                                        <td>{dateTimeToView(data.updatedAt ?? "")}</td>
                                    </tr>
                                </tbody>
                            </table>
                            {issue && (
                                <div className="alert alert-danger col-sm-6 mb-3" role="alert">
                                    {issue}
                                </div>
                            )}
                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="d-flex justify-content-between mb-2">
                                        <button
                                            disabled={loading}
                                            type="button"
                                            className="btn btn-sm btn-brand"
                                            onClick={handleConnect}
                                        >
                                            Подключить
                                        </button>
                                        {data.isAuthenticated && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={handleDelete}
                                            >
                                                Удалить интеграцию
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card card-body font-size-14 py-2">
                            Ознакомьтесь с инструкцией для подключения интеграции с системой отслеживания перейдя по
                            ссылке:
                            <a href={helpUrl} target="_blank" rel="noopener noreferrer">
                                {helpUrl}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    );
}
